import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Scale, Shield, FileText, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { ROBOTS_META_CONTENT } from "../lib/indexing";

const SITE_URL = "https://www.bilan2vie.fr";

/**
 * Page wrapper used by all 3 legal pages.
 * Centralized to keep look consistent (mystical dark theme with halos).
 */
function LegalLayout({ title, subtitle, icon: Icon = Scale, jsonLdPath, children, testId }) {
  const pageTitle = `${title} | Bilan 2 Vie Montélimar`;
  return (
    <main className="pt-24 min-h-screen overflow-x-hidden" data-testid={testId}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={subtitle} />
        <link rel="canonical" href={`${SITE_URL}${jsonLdPath}`} />
        <meta name="robots" content={ROBOTS_META_CONTENT} />
      </Helmet>

      {/* HERO */}
      <section className="relative py-14 md:py-20 overflow-hidden">
        <div className="absolute -top-20 -left-20 w-[500px] h-[500px] rounded-full bg-[#D926A9]/12 blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -right-20 w-[400px] h-[400px] rounded-full bg-[#00E5FF]/8 blur-3xl pointer-events-none" />
        <div className="absolute inset-0 bg-noise pointer-events-none" />

        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#D926A9]/15 border border-[#D926A9]/30 flex items-center justify-center">
              <Icon className="w-7 h-7 text-[#D926A9]" />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl mb-3">{title}</h1>
            <p className="text-[#B59ED1] text-base md:text-lg">{subtitle}</p>
            <p className="text-xs text-[#B59ED1]/70 mt-4">Dernière mise à jour : 29 mai 2026</p>
          </motion.div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="relative pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-noise pointer-events-none" />
        <div className="relative z-10 max-w-3xl mx-auto px-6">
          <div className="rounded-2xl bg-[#261B3B]/60 backdrop-blur-sm border border-white/10 p-6 md:p-10 prose prose-invert max-w-none">
            {children}
          </div>
          <p className="text-center text-xs text-[#B59ED1]/70 mt-8">
            <Link to="/" className="hover:text-white">← Retour à l'accueil</Link>
          </p>
        </div>
      </section>
    </main>
  );
}

/* Shared styles for legal text */
const sectionTitle = "font-serif text-2xl mt-8 mb-3 text-[#F7F3FC]";
const subTitle = "font-medium text-base mt-5 mb-2 text-[#E2D5F8] uppercase tracking-wider text-xs";
const paragraph = "text-[#E2D5F8] leading-relaxed mb-4 text-sm md:text-base";
const list = "text-[#E2D5F8] leading-relaxed mb-4 ml-4 space-y-1.5 text-sm md:text-base list-disc";
const accent = "text-[#D926A9] font-medium";


// ============== MENTIONS LÉGALES ==============
export function LegalNoticePage() {
  return (
    <LegalLayout
      title="Mentions légales"
      subtitle="Informations légales du site bilan2vie.fr"
      icon={Scale}
      jsonLdPath="/mentions-legales"
      testId="legal-notice-page"
    >
      <h2 className={sectionTitle}>1. Éditeur du site</h2>
      <p className={paragraph}>
        Le site <span className={accent}>bilan2vie.fr</span> est édité par :
      </p>
      <ul className={list}>
        <li><strong>Guillaume GOUNDEN</strong> — Auto-entrepreneur (micro-entreprise)</li>
        <li>Nom commercial : <strong>Bilan 2 Vie</strong></li>
        <li>SIRET : <span className={accent}>891 655 326 00025</span></li>
        <li>Adresse : 20 quai du Roubion, 26200 Montélimar, France</li>
        <li>Téléphone : +33 7 68 48 90 56</li>
        <li>Email : <a href="mailto:guillaume@bilan2vie.fr" className="text-[#00E5FF] hover:underline">guillaume@bilan2vie.fr</a></li>
      </ul>
      <p className={paragraph}>
        Le directeur de la publication est Guillaume GOUNDEN.
      </p>

      <h2 className={sectionTitle}>2. Hébergement</h2>
      <p className={paragraph}>
        Le site est hébergé par : <strong>Emergent Labs</strong> (infrastructure cloud).
        Contact : via le support technique de la plateforme Emergent.
      </p>

      <h2 className={sectionTitle}>3. Propriété intellectuelle</h2>
      <p className={paragraph}>
        L'ensemble du contenu présent sur ce site (textes, images, logos, vidéos, illustrations, articles de blog, descriptions de produits et de services, design) est la propriété exclusive de Guillaume GOUNDEN — Bilan 2 Vie, ou de ses partenaires, et est protégé par le droit français et international de la propriété intellectuelle.
      </p>
      <p className={paragraph}>
        Toute reproduction, représentation, modification, publication, transmission, dénaturation, totale ou partielle, du site ou de son contenu, par quelque procédé que ce soit, et sur quelque support que ce soit, est interdite sans autorisation écrite préalable.
      </p>

      <h2 className={sectionTitle}>4. Liens hypertextes</h2>
      <p className={paragraph}>
        Le site peut contenir des liens vers d'autres sites internet. Bilan 2 Vie ne saurait être tenu responsable du contenu de ces sites tiers.
      </p>

      <h2 className={sectionTitle}>5. Données personnelles</h2>
      <p className={paragraph}>
        La collecte et le traitement des données personnelles sont décrits dans la <Link to="/politique-de-confidentialite" className="text-[#00E5FF] hover:underline">Politique de confidentialité</Link>.
      </p>

      <h2 className={sectionTitle}>6. Litiges</h2>
      <p className={paragraph}>
        Les présentes mentions légales sont régies par le droit français. Tout litige sera soumis à la compétence exclusive des tribunaux de Valence (Drôme).
      </p>
    </LegalLayout>
  );
}


// ============== POLITIQUE DE CONFIDENTIALITÉ ==============
export function PrivacyPolicyPage() {
  return (
    <LegalLayout
      title="Politique de confidentialité"
      subtitle="Comment Bilan 2 Vie protège tes données personnelles conformément au RGPD"
      icon={Shield}
      jsonLdPath="/politique-de-confidentialite"
      testId="privacy-policy-page"
    >
      <p className={paragraph}>
        La présente politique de confidentialité explique comment <strong>Bilan 2 Vie</strong> (ci-après « nous ») collecte, utilise et protège tes données personnelles lorsque tu utilises le site <strong>bilan2vie.fr</strong>, conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi française « Informatique et Libertés ».
      </p>

      <h2 className={sectionTitle}>1. Responsable du traitement</h2>
      <p className={paragraph}>
        Guillaume GOUNDEN — Bilan 2 Vie, 20 quai du Roubion, 26200 Montélimar.<br />
        Contact : <a href="mailto:guillaume@bilan2vie.fr" className="text-[#00E5FF] hover:underline">guillaume@bilan2vie.fr</a>
      </p>

      <h2 className={sectionTitle}>2. Données collectées</h2>
      <p className={paragraph}>Nous collectons uniquement les données strictement nécessaires aux services proposés :</p>
      <p className={subTitle}>Lors de la création de compte (Google Auth)</p>
      <ul className={list}>
        <li>Nom et prénom</li>
        <li>Adresse email</li>
        <li>Photo de profil Google (facultative)</li>
      </ul>
      <p className={subTitle}>Lors d'une réservation de consultation</p>
      <ul className={list}>
        <li>Numéro de téléphone</li>
        <li>Date, heure et type de consultation choisie</li>
        <li>Mode (présentiel à Montélimar ou à distance)</li>
      </ul>
      <p className={subTitle}>Lors d'une commande boutique</p>
      <ul className={list}>
        <li>Adresse de livraison (pour Mondial Relay ou domicile)</li>
        <li>Numéro de téléphone</li>
        <li>Historique des commandes</li>
        <li>Données de paiement (traitées exclusivement par Square — voir §5)</li>
      </ul>
      <p className={subTitle}>Données techniques (cookies essentiels uniquement)</p>
      <ul className={list}>
        <li>Cookie de session (token HTTP-only) pour te maintenir connecté</li>
        <li>Aucun cookie publicitaire ou de tracking n'est utilisé</li>
      </ul>

      <h2 className={sectionTitle}>3. Finalités du traitement</h2>
      <p className={paragraph}>Tes données sont utilisées exclusivement pour :</p>
      <ul className={list}>
        <li>Gérer ton compte et tes réservations de consultation</li>
        <li>Traiter tes commandes et expéditions</li>
        <li>T'envoyer des emails transactionnels (confirmation RDV, commande, rappels)</li>
        <li>T'envoyer des notifications push (uniquement si tu y as consenti)</li>
        <li>Respecter nos obligations légales (comptabilité, factures)</li>
      </ul>
      <p className={paragraph}>
        <strong>Nous n'utilisons jamais tes données à des fins de prospection commerciale tierce ni de revente.</strong>
      </p>

      <h2 className={sectionTitle}>4. Confidentialité des consultations</h2>
      <p className={paragraph}>
        <strong className={accent}>Le secret professionnel s'applique à 100%.</strong> Le contenu de tes consultations, les questions posées et les sujets abordés ne sont jamais partagés, ni stockés en clair en dehors d'éventuelles notes privées prises par Guillaume pour ton suivi. Ces notes restent strictement personnelles et confidentielles.
      </p>

      <h2 className={sectionTitle}>5. Sous-traitants et partenaires</h2>
      <p className={paragraph}>
        Pour fonctionner, le site utilise les services suivants — tous conformes RGPD :
      </p>
      <ul className={list}>
        <li><strong>Square</strong> (paiement en ligne) — données bancaires traitées par Square uniquement, jamais stockées par nous</li>
        <li><strong>Brevo (Sendinblue)</strong> (envoi d'emails transactionnels)</li>
        <li><strong>Mondial Relay</strong> (livraison physique des commandes)</li>
        <li><strong>Google Auth</strong> (authentification, géré par Google)</li>
        <li><strong>Emergent Labs</strong> (hébergement cloud)</li>
      </ul>

      <h2 className={sectionTitle}>6. Durée de conservation</h2>
      <ul className={list}>
        <li>Données de compte : tant que ton compte est actif, puis 3 ans après dernière connexion</li>
        <li>Factures et commandes : 10 ans (obligation comptable légale)</li>
        <li>Cookies de session : supprimés à la déconnexion ou après 30 jours d'inactivité</li>
      </ul>

      <h2 className={sectionTitle}>7. Tes droits</h2>
      <p className={paragraph}>Conformément au RGPD, tu disposes des droits suivants :</p>
      <ul className={list}>
        <li><strong>Droit d'accès</strong> à tes données</li>
        <li><strong>Droit de rectification</strong> en cas de données erronées</li>
        <li><strong>Droit à l'effacement</strong> (« droit à l'oubli ») — sous réserve des obligations légales de conservation</li>
        <li><strong>Droit à la portabilité</strong> de tes données dans un format lisible</li>
        <li><strong>Droit d'opposition</strong> aux traitements pour motif légitime</li>
        <li><strong>Droit de retirer ton consentement</strong> à tout moment (notifications push, etc.)</li>
      </ul>
      <p className={paragraph}>
        Pour exercer ces droits, écris-nous à <a href="mailto:guillaume@bilan2vie.fr" className="text-[#00E5FF] hover:underline">guillaume@bilan2vie.fr</a> en précisant l'objet de ta demande. Une réponse te sera apportée sous 30 jours maximum.
      </p>
      <p className={paragraph}>
        Tu peux également introduire une réclamation auprès de la <strong>CNIL</strong> (Commission Nationale de l'Informatique et des Libertés) : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#00E5FF] hover:underline">www.cnil.fr</a>.
      </p>

      <h2 className={sectionTitle}>8. Sécurité</h2>
      <p className={paragraph}>
        Nous mettons en œuvre des mesures techniques et organisationnelles appropriées : chiffrement HTTPS, hashage des mots de passe (si utilisés), accès limités aux données, sauvegardes régulières, hébergement dans l'Union européenne.
      </p>

      <h2 className={sectionTitle}>9. Mise à jour de cette politique</h2>
      <p className={paragraph}>
        Cette politique peut évoluer. Toute modification substantielle te sera notifiée. La date de dernière mise à jour est indiquée en haut de cette page.
      </p>
    </LegalLayout>
  );
}


// ============== CGV ==============
export function CguPage() {
  return (
    <LegalLayout
      title="Conditions générales de vente"
      subtitle="Conditions applicables aux consultations et achats sur bilan2vie.fr"
      icon={FileText}
      jsonLdPath="/cgv"
      testId="cgv-page"
    >
      <h2 className={sectionTitle}>1. Objet</h2>
      <p className={paragraph}>
        Les présentes Conditions Générales de Vente (CGV) régissent les ventes de prestations de service (consultations de voyance, Pranic Healing, Bilan de Vie, soins énergétiques, événements) et de produits (articles de la boutique ésotérique) proposés par Bilan 2 Vie, depuis le site bilan2vie.fr.
      </p>

      <h2 className={sectionTitle}>2. Prix</h2>
      <p className={paragraph}>
        Tous les prix sont indiqués en euros <strong>nets</strong>. <strong>Bilan 2 Vie n'est pas assujetti à la TVA</strong> (article 293 B du Code Général des Impôts — régime micro-entreprise). Les prix affichés correspondent donc au montant final à régler, sans taxe additionnelle.
      </p>

      <h2 className={sectionTitle}>3. Réservation d'une consultation</h2>
      <p className={paragraph}>
        La réservation d'une consultation se fait en ligne via le site, en choisissant un créneau disponible. Le paiement s'effectue au moment de la séance (en boutique ou via lien Square pour le distanciel), ou en ligne si le service le précise.
      </p>
      <p className={paragraph}>
        <strong>Politique d'annulation :</strong> toute annulation ou modification doit être faite au minimum <strong>3 heures avant le rendez-vous</strong>. Au-delà, la consultation est considérée comme due.
      </p>

      <h2 className={sectionTitle}>4. Commande boutique</h2>
      <p className={paragraph}>
        Les commandes sont validées après paiement Square (CB sécurisée) ou réservation pour le Click & Collect (paiement en boutique au retrait). Tu reçois une confirmation par email après chaque commande.
      </p>

      <h2 className={sectionTitle}>5. Livraison</h2>
      <ul className={list}>
        <li><strong>Click & Collect</strong> : retrait à la boutique (20 quai du Roubion, Montélimar) selon les horaires d'ouverture</li>
        <li><strong>Mondial Relay (point relais)</strong> : 4,90 € — livraison en 3 à 5 jours ouvrés</li>
        <li><strong>Mondial Relay (domicile)</strong> : 6,90 € — livraison en 2 à 4 jours ouvrés</li>
      </ul>
      <p className={paragraph}>
        Les délais sont indicatifs et démarrent à la validation du paiement.
      </p>

      <h2 className={sectionTitle}>6. Droit de rétractation</h2>
      <p className={paragraph}>
        Conformément à l'article L.221-18 du Code de la consommation, tu disposes d'un <strong>délai de 14 jours</strong> pour retourner les produits achetés en ligne, à compter de leur réception. Les produits doivent être retournés dans leur état d'origine, non utilisés.
      </p>
      <p className={paragraph}>
        <strong>Exceptions</strong> (article L.221-28) — pas de rétractation possible pour :
      </p>
      <ul className={list}>
        <li>Les consultations déjà réalisées (prestation entièrement exécutée)</li>
        <li>Les bougies rituelles personnalisées, élixirs sur-mesure et produits scellés ouverts (pour des raisons d'hygiène)</li>
        <li>Les cartes cadeaux et coffrets activés</li>
      </ul>

      <h2 className={sectionTitle}>7. Codes promotionnels</h2>
      <p className={paragraph}>
        Les codes promo sont nominatifs ou globaux selon leur configuration. Ils ne sont valables que dans la limite de leur date d'expiration et du nombre d'utilisations restant. Un seul code peut être appliqué par commande. Les codes promo ne sont pas cumulables et ne peuvent être convertis en espèces.
      </p>

      <h2 className={sectionTitle}>8. Responsabilité</h2>
      <p className={paragraph}>
        Les consultations de voyance, Pranic Healing, numérologie et tous les services spirituels proposés constituent un <strong>accompagnement à titre informatif</strong> et n'ont en aucun cas vocation à remplacer un avis médical, psychologique ou juridique. Le client conserve son libre arbitre et reste seul responsable de ses choix et décisions.
      </p>

      <h2 className={sectionTitle}>9. Réclamations</h2>
      <p className={paragraph}>
        Pour toute réclamation, contacte-nous à <a href="mailto:guillaume@bilan2vie.fr" className="text-[#00E5FF] hover:underline">guillaume@bilan2vie.fr</a> ou au +33 7 68 48 90 56. Nous nous engageons à répondre dans les meilleurs délais.
      </p>

      <h2 className={sectionTitle}>10. Séjours & Retraites spirituelles</h2>
      <p id="sejours-retraites" className={paragraph}>
        Cette section régit spécifiquement les inscriptions aux séjours et retraites spirituels organisés par Bilan 2 Vie (ci-après « le Séjour »), commandables depuis les pages <a href="/retraites" className="text-[#00E5FF] hover:underline">bilan2vie.fr/retraites</a>. En cochant la case « J'accepte les CGV » au moment de la réservation, le participant reconnaît en avoir pris connaissance et les accepter sans réserve.
      </p>

      <h3 className={paragraph + " font-medium mt-6"}>10.1 Modalités de réservation et de paiement</h3>
      <ul className={list}>
        <li><strong>Acompte de 30 %</strong> versé au moment de la réservation en ligne (CB ou Klarna 3-4× sans frais), ou <strong>paiement intégral</strong> selon le choix du participant.</li>
        <li><strong>Solde de 70 %</strong> dû au plus tard <strong>30 jours avant le début du Séjour</strong> (« J-30 »). Un rappel est envoyé par email à J-45 puis J-35.</li>
        <li>Pour toute réservation effectuée à moins de 30 jours du Séjour, le <strong>paiement intégral est exigé</strong> à l'inscription.</li>
        <li>Le règlement peut également être effectué <strong>en espèces à la boutique</strong> (20 Quai du Roubion, 26200 Montélimar) — un reçu papier est alors remis au participant, valant preuve d'inscription.</li>
      </ul>

      <h3 className={paragraph + " font-medium mt-6"}>10.2 Annulation par le participant</h3>
      <ul className={list}>
        <li><strong>L'acompte de 30 % est non remboursable</strong> dès que la place est bloquée, quelle que soit la date d'annulation.</li>
        <li><strong>Annulation entre J-45 et J-30</strong> : l'acompte reste acquis à Bilan 2 Vie. Si le solde de 70 % a déjà été réglé, il est <strong>intégralement remboursé</strong>.</li>
        <li><strong>Annulation à moins de J-30</strong> : <strong>aucun remboursement possible</strong>. La totalité des sommes versées reste acquise, sauf dans les deux cas suivants :
          <ul className={list}>
            <li>Une personne inscrite sur la <strong>liste d'attente</strong> accepte de reprendre la place — dans ce cas, le participant est remboursé du solde (l'acompte reste acquis) ;</li>
            <li>Le participant <strong>trouve lui-même un remplaçant</strong> qui accepte de reprendre l'inscription aux mêmes conditions — mêmes modalités de remboursement (solde uniquement).</li>
          </ul>
        </li>
        <li><strong>Non-règlement du solde à J-30</strong> : la réservation est <strong>automatiquement annulée</strong> et la chambre libérée. L'acompte reste acquis à Bilan 2 Vie.</li>
        <li><strong>Non-présentation le jour du Séjour</strong> (no-show) : aucun remboursement possible.</li>
      </ul>
      <p className={paragraph}>
        Il est fortement recommandé au participant de souscrire une <strong>assurance annulation</strong> personnelle (proposée notamment par sa banque, sa carte bancaire premium, ou un contrat d'assurance voyage). Bilan 2 Vie ne fournit pas d'assurance annulation.
      </p>

      <h3 className={paragraph + " font-medium mt-6"}>10.3 Annulation par Bilan 2 Vie</h3>
      <ul className={list}>
        <li><strong>Nombre minimum de participants non atteint</strong> (le cas échéant, précisé sur chaque séjour) : le Séjour peut être annulé jusqu'à J-30. Dans ce cas, <strong>remboursement intégral</strong> des sommes versées OU report sur un séjour futur au choix du participant.</li>
        <li><strong>Force majeure</strong> (intempéries majeures, décision administrative, pandémie, indisponibilité du lieu, etc.) : <strong>remboursement intégral</strong> ou report gratuit.</li>
        <li><strong>Annulation de convenance</strong> par Bilan 2 Vie (autre motif) : <strong>remboursement intégral</strong>.</li>
      </ul>

      <h3 className={paragraph + " font-medium mt-6"}>10.4 Ce qui est inclus / non inclus</h3>
      <p className={paragraph}><strong>Inclus</strong> dans le prix du Séjour :</p>
      <ul className={list}>
        <li>L'hébergement pour la durée du Séjour selon la formule choisie (chambre simple ou double)</li>
        <li>Les repas (végétariens de saison, adaptés aux régimes signalés à l'inscription)</li>
        <li>Toutes les activités du programme (méditations, ateliers, soins collectifs, marches)</li>
        <li>L'accompagnement de Guillaume durant toute la durée du Séjour</li>
      </ul>
      <p className={paragraph}><strong>Non inclus</strong> :</p>
      <ul className={list}>
        <li>Le <strong>transport</strong> aller-retour jusqu'au lieu du Séjour (des covoiturages sont organisés entre participants à titre indicatif)</li>
        <li>Les <strong>consultations individuelles</strong> supplémentaires (voyance, soins énergétiques) pouvant être proposées sur place à tarif préférentiel</li>
        <li>Les <strong>dépenses personnelles</strong> (achats en boutique, extras, pourboires éventuels au personnel du lieu d'accueil)</li>
        <li>L'<strong>assurance annulation</strong> personnelle</li>
      </ul>

      <h3 className={paragraph + " font-medium mt-6"}>10.5 Informations médicales et alimentaires</h3>
      <p className={paragraph}>
        Le participant s'engage à signaler à l'inscription toute <strong>allergie, intolérance alimentaire ou contre-indication médicale</strong> susceptible d'affecter sa participation aux activités (méditation, marche, jeûne partiel éventuel). Bilan 2 Vie décline toute responsabilité en cas de non-déclaration ou de déclaration inexacte. Les Séjours ne remplacent en aucun cas un suivi médical ; le participant conserve la responsabilité de sa santé et doit consulter son médecin en cas de doute.
      </p>

      <h3 className={paragraph + " font-medium mt-6"}>10.6 Comportement et respect du lieu</h3>
      <ul className={list}>
        <li>Le participant s'engage à respecter les autres participants, l'intervenant, le personnel du lieu d'accueil et l'environnement naturel.</li>
        <li>La <strong>consommation d'alcool</strong> et l'usage de <strong>substances illicites</strong> sont interdits pendant toute la durée du Séjour.</li>
        <li>Bilan 2 Vie se réserve le droit d'<strong>exclure sans remboursement</strong> tout participant dont le comportement porterait atteinte au bon déroulement du Séjour ou à la sécurité d'autrui.</li>
      </ul>

      <h3 className={paragraph + " font-medium mt-6"}>10.7 Droit à l'image</h3>
      <p className={paragraph}>
        Des photos ou vidéos peuvent être prises pendant le Séjour à des fins de communication (site web, réseaux sociaux, presse). Aucune image ne sera diffusée sans le consentement écrit du participant. Un formulaire de consentement est proposé sur place, la signature reste facultative. Le participant peut à tout moment demander le retrait d'une image publiée en écrivant à guillaume@bilan2vie.fr.
      </p>

      <h3 className={paragraph + " font-medium mt-6"}>10.8 Responsabilité de Bilan 2 Vie</h3>
      <p className={paragraph}>
        Les activités spirituelles proposées durant le Séjour (méditations, soins énergétiques, ateliers) constituent un <strong>accompagnement à titre informatif et d'exploration personnelle</strong>. Elles ne remplacent en aucun cas un avis médical, psychologique ou juridique. Bilan 2 Vie ne saurait être tenu responsable des choix, décisions ou évolutions personnelles du participant à l'issue du Séjour. La responsabilité de Bilan 2 Vie est limitée aux dommages directs prouvés, dans la limite du montant du Séjour réglé.
      </p>

      <h3 className={paragraph + " font-medium mt-6"}>10.9 Données personnelles</h3>
      <p className={paragraph}>
        Les données collectées à l'inscription (identité, contact, régime alimentaire, informations médicales éventuelles) sont utilisées exclusivement pour l'organisation du Séjour, conformément à la <a href="/politique-confidentialite" className="text-[#00E5FF] hover:underline">politique de confidentialité</a>. La preuve d'acceptation des présentes CGV (date, heure, adresse IP, version) est conservée à des fins probatoires pendant 5 ans après la fin du Séjour.
      </p>

      <h2 className={sectionTitle}>11. Droit applicable</h2>
      <p className={paragraph}>
        Les présentes CGV sont régies par le droit français. En cas de litige, et après tentative de résolution amiable, les tribunaux de Valence seront seuls compétents.
      </p>
    </LegalLayout>
  );
}


// ============== POLITIQUE DES CONSULTATIONS ==============
export function ConsultationPolicyPage() {
  return (
    <LegalLayout
      title="Politique des consultations"
      subtitle="Voyance, guidance et soins énergétiques chez Bilan 2 Vie"
      icon={Sparkles}
      jsonLdPath="/politique-consultations"
      testId="consultation-policy-page"
    >
      <p className={paragraph}>
        Les consultations de voyance, guidances et soins proposés par <strong>Bilan 2 Vie</strong> sont réalisés dans un cadre <strong className={accent}>sérieux, confidentiel, respectueux et bienveillant</strong>. Avant de réserver, merci de prendre connaissance des points suivants.
      </p>

      <h2 className={sectionTitle}>1. Heure de rendez-vous et accès à la boutique</h2>
      <p className={paragraph}>
        L'horaire indiqué correspond à l'heure à laquelle le/la consultant(e) peut entrer dans la boutique ou doit se présenter. Par respect pour les séances en cours, <strong>merci de ne pas arriver en avance</strong> : toute arrivée anticipée pourra entraîner une attente à l'extérieur.
      </p>
      <p className={paragraph}>
        À l'heure du rendez-vous, présente-toi à la <strong className={accent}>porte rouge "Bilan 2 Vie – Entrée"</strong>.
      </p>
      <p className={paragraph}>
        <strong>Les accompagnants ne sont pas acceptés en consultation/soin.</strong>
      </p>

      <h2 className={sectionTitle}>2. Cadre et confidentialité</h2>
      <p className={paragraph}>
        Chaque séance est menée avec écoute, respect, sans jugement et dans la plus stricte confidentialité. Les échanges restent privés.
      </p>

      <h2 className={sectionTitle}>3. Nature de la prestation</h2>
      <p className={paragraph}>
        La voyance / guidance / soin constitue un <strong>accompagnement spirituel et intuitif</strong>. Elle ne remplace en aucun cas un avis ou un suivi médical, psychologique, juridique ou financier.
      </p>

      <h2 className={sectionTitle}>4. Responsabilité du/de la consultant(e)</h2>
      <p className={paragraph}>
        Les messages transmis en séance ont pour objectif d'éclairer et d'accompagner. Le/la consultant(e) demeure libre de ses choix et pleinement responsable de ses décisions.
      </p>

      <h2 className={sectionTitle}>5. Focalisation de la consultation</h2>
      <p className={paragraph}>
        Les consultations portent <strong>exclusivement sur le/la consultant(e)</strong> ou sur une personne directement liée à sa problématique. Les questions intrusives concernant des tiers sans lien direct ne sont pas abordées.
      </p>

      <h2 className={sectionTitle}>6. Mineurs</h2>
      <p className={paragraph}>
        Les consultations sont <strong className={accent}>strictement réservées aux personnes majeures</strong>. Les mineurs ne sont pas reçus, même avec l'accord, la demande ou la présence d'un parent ou tuteur.
      </p>

      <h2 className={sectionTitle}>7. Sujets exclus</h2>
      <p className={paragraph}>Les consultations n'abordent pas :</p>
      <ul className={list}>
        <li>la santé,</li>
        <li>le décès,</li>
        <li>les questions intrusives relatives à la vie privée d'un tiers.</li>
      </ul>

      <h2 className={sectionTitle}>8. Rendez-vous, modification et annulation</h2>
      <p className={paragraph}>
        Toute réservation engage le créneau choisi. Le rendez-vous peut être modifié ou annulé <strong>sans frais jusqu'à 3 heures avant l'horaire prévu</strong>. Moins de 3 heures avant, la consultation / le soin est dû(e), sauf cas exceptionnel justifié et laissé à l'appréciation de Bilan 2 Vie.
      </p>

      <h2 className={sectionTitle}>9. Retard, absence et paiement</h2>
      <p className={paragraph}>
        En cas de retard, la séance pourra être écourtée sans réduction tarifaire afin de respecter les rendez-vous suivants. En cas d'absence sans prévenir, la prestation est due. Le paiement s'effectue selon les modalités communiquées lors de la réservation (en boutique ou à distance).
      </p>
    </LegalLayout>
  );
}
